/**
 * EmptyState Component
 *
 * Displays helpful messaging when lists, tables, or search results are empty.
 * Improves UX by guiding users on next steps.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';

export interface EmptyStateProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * Icon to display (React element)
   */
  icon?: React.ReactNode;
  /**
   * Title text
   */
  title: string;
  /**
   * Description text
   */
  description?: string;
  /**
   * Primary action button
   */
  action?: React.ReactNode;
  /**
   * Secondary action (usually a link)
   */
  secondaryAction?: React.ReactNode;
  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * EmptyState Component
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<Package className="h-12 w-12" />}
 *   title="Keine Produkte gefunden"
 *   description="Erstellen Sie Ihr erstes Produkt, um loszulegen."
 *   action={<Button variant="primary">Produkt erstellen</Button>}
 *   secondaryAction={<Button variant="ghost">Dokumentation</Button>}
 * />
 * ```
 */
const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      className,
      icon,
      title,
      description,
      action,
      secondaryAction,
      size = 'md',
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const sizeClasses = {
      sm: {
        container: 'py-8',
        icon: 'mb-3',
        title: 'text-base',
        description: 'text-sm',
        actions: 'mt-4 gap-2',
      },
      md: {
        container: 'py-12',
        icon: 'mb-4',
        title: 'text-lg',
        description: 'text-base',
        actions: 'mt-6 gap-3',
      },
      lg: {
        container: 'py-16',
        icon: 'mb-6',
        title: 'text-xl',
        description: 'text-lg',
        actions: 'mt-8 gap-4',
      },
    };

    const classes = sizeClasses[size];

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center text-center',
          classes.container,
          className
        )}
        role="region"
        aria-label={t('a11y.emptyState')}
        {...props}
      >
        {/* Icon */}
        {icon && (
          <div
            className={cn(
              'text-neutral-400 dark:text-neutral-500',
              classes.icon
            )}
            aria-hidden="true"
          >
            {icon}
          </div>
        )}

        {/* Title */}
        <h3
          className={cn(
            'font-semibold text-neutral-900 dark:text-neutral-50',
            classes.title
          )}
        >
          {title}
        </h3>

        {/* Description */}
        {description && (
          <p
            className={cn(
              'mt-2 max-w-md text-neutral-600 dark:text-neutral-400',
              classes.description
            )}
          >
            {description}
          </p>
        )}

        {/* Actions */}
        {(action || secondaryAction) && (
          <div className={cn('flex flex-wrap items-center justify-center', classes.actions)}>
            {action}
            {secondaryAction}
          </div>
        )}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

export { EmptyState };
